import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';

/**
 * GET /api/Patients/GetEncounterByEncounterId?AppointmentId={id}
 *
 * Mirrors Flutter `SchedulingDataSourceImpl.fetchEncounterByAppointmentId`
 * (`scheduling_data_source_impl.dart:674-688`). Used by the
 * ViewAppointmentPanel's Docs tab (PROD-797) and Vitals tab (PROD-795)
 * to determine whether an encounter has been created for the
 * appointment yet — both tabs scope their content to the encounter
 * when one exists.
 *
 * Wire shape: GET with the appointment id as a query parameter (NOT a
 * path segment despite the endpoint name).
 *
 * Response (Flutter `EncounterModel.fromMap`): an envelope where the
 * `identifier` field carries the encounter id when one exists. The
 * Flutter consumer branches on `model.identifier != null` to know
 * whether to create-on-demand or use the existing id. Mirror the same
 * pattern here.
 *
 * AbortSignal forwarded per the project rule.
 *
 * TODO (PROD-797 PR 6 / Samer review PR #134 §1):
 * --------------------------------------------------------------------
 * The "no encounter exists" wire shape is NOT YET curl-verified. The
 * consumer reads `data?.identifier ?? null` — fail-safe in both the
 * "200 with null identifier" and "200 with missing identifier" cases.
 * But if the backend returns:
 *   - A 404 → React Query treats this as `isError` and the consumer
 *     never sees `data` (the Docs tab handles isError separately, so
 *     this misbehaves — the "no encounter yet" copy would never show).
 *   - A 200 with a DIFFERENT shape (e.g. `{ encounter: null }`) → the
 *     consumer would silently miss a real encounter.
 *
 * Capture a no-encounter curl before PR 6 lands (encounter
 * create-on-demand). If the backend uses 404, this hook needs to
 * special-case 404 → `{ identifier: null }` rather than propagating
 * as a query error.
 * --------------------------------------------------------------------
 */

export interface EncounterModel {
  /** Encounter id — the only field downstream consumers read. */
  identifier?: string | null;
  appointment?: string | null;
  subject?: string | null;
  status?: string | null;
  serviceProvider?: string | null;
  locationName?: string | null;
  duration?: number | null;
  reasonForVisit?: string | null;
  reasonForVisitName?: string | null;
  [key: string]: unknown;
}

export interface UseEncounterByAppointmentParams {
  appointmentId: string | null | undefined;
  enabled?: boolean;
}

export function useEncounterByAppointment(
  params: UseEncounterByAppointmentParams,
): UseQueryResult<EncounterModel> {
  const { appointmentId, enabled = true } = params;

  return useQuery<EncounterModel>({
    queryKey: ['encounter-by-appointment', appointmentId],
    enabled: enabled && !!appointmentId,
    queryFn: async ({ signal }) => {
      // 404 = "no encounter yet for this appointment" — a valid empty
      // state, NOT an error. Flutter parity: the .dart consumer
      // branches on `model.identifier != null`; the backend signals
      // absence with a 404 here, so we translate to the empty-model
      // shape rather than propagating an isError state that would
      // surface a red toast on the patient/appointment view.
      try {
        return await get<EncounterModel>(
          `/api/Patients/GetEncounterByEncounterId?AppointmentId=${encodeURIComponent(
            appointmentId as string,
          )}`,
          { signal },
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { identifier: null };
        }
        throw err;
      }
    },
  });
}
